/**
 * AutobuildDecisionCard — the AutobuildPanel for a Plate task.
 *
 * The pipeline puts a task on a director's Plate when a build needs them
 * (awaitingApproval, blocked, …) and on the reporter's when it has a
 * question. Those tasks carry the report's /home/bugs deep link as
 * `sourcePath`; this card follows it, listens to the report, and renders
 * the same panel /home/bugs shows — so Approve / Request changes and the
 * answer box work without leaving the Plate.
 */

import { useEffect, useState } from 'react';
import { subscribeBugReport, type BugReport } from '@/services/bug-report.service';
import { useDirectorAccess } from '@/hooks/useDirectorAccess';
import { bugReportIdFromPath } from '@shared/domain/bug-reports';
import { AutobuildPanel } from './AutobuildPanel';

interface Props {
  /** The task's `sourcePath` — anything that isn't a /home/bugs link renders nothing. */
  sourcePath: string | undefined;
  viewer: { id: string; name: string };
}

export function AutobuildDecisionCard({ sourcePath, viewer }: Props) {
  const reportId = bugReportIdFromPath(sourcePath);
  const { isDirector, ownerIds } = useDirectorAccess();
  const [report, setReport] = useState<BugReport | null>(null);

  useEffect(() => {
    if (!reportId) {
      setReport(null);
      return;
    }
    return subscribeBugReport(reportId, setReport);
  }, [reportId]);

  if (!reportId || !report?.autobuild) return null;

  return (
    <AutobuildPanel
      report={report}
      viewer={{ id: viewer.id, name: viewer.name, isDirector, ownerIds }}
      via="plate"
    />
  );
}
